import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export { default as Button } from './Button.vue';

const BASE_CLASSES = [
    'inline-flex',
    'items-center',
    'justify-center',
    'relative',
    'whitespace-nowrap',
    'rounded-full',
    '[&_svg]:pointer-events-none',
    '[&_svg]:shrink-0',
    'shrink-0',
    'outline-none',
    'focus-visible:border-ring',
    'focus-visible:ring-ring/70',
    'focus-visible:ring-[3px]',
    'disabled:pointer-events-none',
    'font-semibold',
    'transition-all',
].join(' ');

export const buttonVariants = cva(BASE_CLASSES, {
    variants: {
        variant: {
            brand: 'bg-background-brand-primary text-content-primary-inverse hover:opacity-85 active:opacity-70 disabled:opacity-40',
            primary: [
                'bg-background-primary-inverse',
                'text-content-primary-inverse',
                'hover:opacity-85',
                'active:opacity-70',
                'disabled:opacity-40',
                'inversed:bg-background-primary',
                'inversed:text-content-primary',
            ].join(' '),
            secondary: [
                'border',
                'border-border-tertiary',
                'text-content-primary',
                'hover:border-border-primary',
                'disabled:text-content-subdued',
                'inversed:border-border-tertiary-inverse',
                'inversed:text-content-primary-inverse',
                'inversed:hover:border-border-primary-inverse',
                'inversed:active:bg-white/10',
                'inversed:disabled:text-content-subdued-inverse',
            ].join(' '),
            tertiary: [
                'border',
                'border-transparent',
                'hover:border-border-tertiary',
                'active:border-border-primary',
                'disabled:text-content-subdued',
                'inversed:text-content-primary-inverse',
                'inversed:hover:border-border-tertiary-inverse',
                'inversed:active:border-border-primary-inverse',
                'inversed:disabled:text-content-subdued-inverse',
            ].join(' '),
        },
        size: {
            sm: "h-8 px-[10px] gap-0.5 text-sm font-medium [&_svg:not([class*='size-'])]:size-4 ",
            md: "h-10 px-[12px] text-sm [&_svg:not([class*='size-'])]:size-5",
            lg: "h-12 px-[16px] [&_svg:not([class*='size-'])]:size-6 gap-3",
            xl: "h-14 px-[24px] [&_svg:not([class*='size-'])]:size-6 gap-4",
        },
        icon: {
            true: 'p-0 aspect-square',
            false: null,
        },
        loading: {
            true: '[&_[data-spinner]]:animate-spin',
            false: null,
        },
    },
    compoundVariants: [
        {
            variant: 'brand',
            loading: true,
            class: '[&>*]:invisible text-transparent opacity-40 [&_[data-spinner]]:text-content-primary-inverse',
        },
        {
            variant: 'primary',
            loading: true,
            class: '[&>*]:invisible text-transparent opacity-40 [&_[data-spinner]]:text-content-primary-inverse',
        },
        {
            variant: 'secondary',
            loading: true,
            class: '[&>*]:invisible text-transparent! [&_[data-spinner]]:text-content-subdued',
        },
        {
            variant: 'tertiary',
            loading: true,
            class: '[&>*]:invisible text-transparent! [&_[data-spinner]]:text-content-subdued',
        },
    ],
    defaultVariants: {
        variant: 'primary',
        size: 'md',
        icon: false,
        loading: false,
    },
});

export type ButtonVariants = VariantProps<typeof buttonVariants>;
